import React from 'react';
import { useTranslation } from 'react-i18next';
import { ShieldCheck, Car, Flag, Clock, GraduationCap, Briefcase, Siren, Zap } from 'lucide-react';
import type { InvoiceStream, PoolFactuurRegel } from '../services/invoiceApi';

/**
 * Regeltabel van een pool-verzamelfactuur.
 *
 * Drie stromen, drie kolomindelingen:
 *  - TRAJECT_BATCH     dossiercode, sessie, handelsnaam, uren, bedrag
 *  - ANONYMOUS_VP      meldingscode, omschrijving, handelsnaam, bedrag
 *                      (géén datum- of urenkolom: die zijn herleidbaar en
 *                      staan niet eens in de response)
 *  - PROJECT_MILESTONE projectcode, mijlpaal, handelsnaam, bedrag
 *  - ACADEMY           registratienummer, categorie, opleider, bedrag
 *                      (alleen het werkgeversdeel; een eventuele privé-
 *                      bijbetaling van de medewerker staat er niet op)
 *  - B2B               b2b-code, dienst, handelsnaam, uren, bedrag
 *  - CRISIS            alarmcode, uitruk/reiskosten, handelsnaam, uren, bedrag
 *  - ADHOC             transactie-ID, omschrijving, handelsnaam, bedrag
 *                      (géén urenkolom: bij medewerker-opvolging herleidbaar)
 */

interface Props {
  stream: InvoiceStream;
  regels: PoolFactuurRegel[];
  nettoOmzet: string | number;
  platformFee: string | number;
  platformFeePercentage: string | number;
  btwBedrag?: string | number | null;
  totaal: string | number;
  /** Fiat-bewijsblok bij een mijlpaalfactuur */
  fiatLog?: string | null;
}

const euro = (waarde: string | number | null | undefined): string => {
  const getal = typeof waarde === 'string' ? parseFloat(waarde) : waarde ?? 0;
  return new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(getal || 0);
};

const REGEL_ICOON: Record<PoolFactuurRegel['line_type'], React.ComponentType<{ className?: string }>> = {
  SESSIE: Clock,
  REISTOESLAG: Car,
  MILESTONE: Flag,
  NACALCULATIE: Clock,
  ACADEMY: GraduationCap,
  B2B: Briefcase,
  UITRUK: Siren,
  REISKOSTEN: Car,
  ADHOC_MEDEWERKER: ShieldCheck,
  ADHOC_INTERN: Zap,
};

export const PoolFactuurRegels: React.FC<Props> = ({
  stream,
  regels,
  nettoOmzet,
  platformFee,
  platformFeePercentage,
  btwBedrag,
  totaal,
  fiatLog,
}) => {
  const { t } = useTranslation('employer');
  const anoniem = stream === 'ANONYMOUS_VP';
  const academy = stream === 'ACADEMY';
  const zonderUren = anoniem || academy || stream === 'ADHOC';

  const kolommen = zonderUren ? 4 : 5;

  // Compliance Vetting Tiers (migratie 175): TRAJECT_BATCH-regels onder de twee fiscale koppen
  const gesplitst = regels.some((r) => r.fiscale_categorie);
  const groepen: { categorie: PoolFactuurRegel['fiscale_categorie']; regels: PoolFactuurRegel[] }[] = gesplitst
    ? (['ARBO_VRIJGESTELD', 'WKR_VRIJE_RUIMTE'] as const)
        .map((categorie) => ({
          categorie,
          regels: regels.filter((r) => (r.fiscale_categorie ?? 'WKR_VRIJE_RUIMTE') === categorie),
        }))
        .filter((g) => g.regels.length > 0)
    : [{ categorie: null, regels }];

  const codeKop =
    stream === 'ANONYMOUS_VP'
      ? t('poolInvoice.columnMeldingscode')
      : stream === 'PROJECT_MILESTONE'
      ? t('poolInvoice.columnProjectCode')
      : academy
      ? t('poolInvoice.columnRegistratienummer')
      : stream === 'B2B' || stream === 'CRISIS' || stream === 'ADHOC'
      ? t('poolInvoice.columnReferentie')
      : t('poolInvoice.columnDossierCode');

  return (
    <div className="mt-4 border-t border-gray-200 pt-4">
      {anoniem && (
        <div className="mb-4 flex items-start gap-2 rounded-md bg-amber-50 border border-amber-200 p-3">
          <ShieldCheck className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <p className="text-sm text-amber-900">{t('poolInvoice.anonymousNotice')}</p>
        </div>
      )}
      {academy && (
        <div className="mb-4 flex items-start gap-2 rounded-md bg-emerald-50 border border-emerald-200 p-3">
          <ShieldCheck className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
          <p className="text-sm text-emerald-900">{t('poolInvoice.academyNotice')}</p>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left text-gray-500 border-b border-gray-200">
              <th className="py-2 pr-4 font-medium">{codeKop}</th>
              <th className="py-2 pr-4 font-medium">{t('poolInvoice.columnDescription')}</th>
              <th className="py-2 pr-4 font-medium">{academy ? t('poolInvoice.columnProvider') : t('poolInvoice.columnTradeName')}</th>
              {!zonderUren && (
                <th className="py-2 pr-4 font-medium text-right">{t('poolInvoice.columnHours')}</th>
              )}
              <th className="py-2 font-medium text-right">{t('poolInvoice.columnAmount')}</th>
            </tr>
          </thead>
          {groepen.map(({ categorie, regels: groepRegels }) => (
            <tbody key={categorie ?? 'alle'}>
              {categorie && (
                <tr className={categorie === 'ARBO_VRIJGESTELD' ? 'bg-emerald-50' : 'bg-amber-50'}>
                  <td colSpan={kolommen} className={`py-2 px-2 text-xs font-semibold ${categorie === 'ARBO_VRIJGESTELD' ? 'text-emerald-900' : 'text-amber-900'}`}>
                    {t(`poolInvoice.fiscaal.${categorie}`)}
                  </td>
                </tr>
              )}
              {groepRegels.map((regel) => {
                const Icoon = REGEL_ICOON[regel.line_type] ?? Clock;
                return (
                  <tr key={regel.id} className="border-b border-gray-100 last:border-0">
                    <td className="py-2 pr-4 font-mono text-xs text-gray-700 whitespace-nowrap">
                      {regel.referentie_code}
                    </td>
                    <td className="py-2 pr-4 text-gray-900">
                      <span className="inline-flex items-center gap-2">
                        <Icoon className="w-4 h-4 text-gray-400 shrink-0" />
                        {regel.omschrijving}
                      </span>
                    </td>
                    <td className="py-2 pr-4 text-gray-700">{regel.kvk_handelsnaam}</td>
                    {!zonderUren && (
                      <td className="py-2 pr-4 text-right text-gray-700 whitespace-nowrap">
                        {regel.uren
                          ? t('poolInvoice.hoursValue', { uren: parseFloat(regel.uren).toFixed(1) })
                          : t('poolInvoice.flatRate')}
                      </td>
                    )}
                    <td className="py-2 text-right font-medium text-gray-900 whitespace-nowrap">
                      {euro(regel.regel_bedrag)}
                    </td>
                  </tr>
                );
              })}
              {categorie && (
                <tr className="border-b border-gray-200">
                  <td colSpan={kolommen - 1} className="py-2 pr-4 text-right text-xs text-gray-600">
                    {t('poolInvoice.fiscaal.subtotaal')}
                  </td>
                  <td className="py-2 text-right text-xs font-semibold text-gray-900 whitespace-nowrap">
                    {euro(groepRegels.reduce((som, r) => som + (parseFloat(r.regel_bedrag) || 0), 0))}
                  </td>
                </tr>
              )}
            </tbody>
          ))}
        </table>
      </div>

      {fiatLog && (
        <p className="mt-3 font-mono text-xs text-gray-500 break-all">{fiatLog}</p>
      )}

      <dl className="mt-4 space-y-1 text-sm border-t border-gray-200 pt-3">
        <div className="flex justify-between">
          <dt className="text-gray-600">{academy ? t('poolInvoice.subtotalAcademy') : t('poolInvoice.subtotalNet')}</dt>
          <dd className="font-medium text-gray-900">{euro(nettoOmzet)}</dd>
        </div>
        {!academy && (
          <div className="flex justify-between">
            <dt className="text-gray-600">
              {t('poolInvoice.platformFee', { percentage: parseFloat(String(platformFeePercentage || 15)).toFixed(0) })}
            </dt>
            <dd className="font-medium text-gray-900">{euro(platformFee)}</dd>
          </div>
        )}
        {btwBedrag != null && (
          <div className="flex justify-between">
            <dt className="text-gray-600">{t('poolInvoice.vat')}</dt>
            <dd className="font-medium text-gray-900">{euro(btwBedrag)}</dd>
          </div>
        )}
        <div className="flex justify-between border-t border-gray-200 pt-2 mt-2">
          <dt className="font-semibold text-gray-900">{t('poolInvoice.totalDue')}</dt>
          <dd className="font-semibold text-gray-900">{euro(totaal)}</dd>
        </div>
      </dl>
    </div>
  );
};

export default PoolFactuurRegels;
